<main class="overflow-x-hidden px-4 pt-8 md:pt-24">

  <div class="mx-auto max-w-6xl mb-16">

    <img class="w-36 sm:w-48 mb-3" [src]="assetsPath + 'ff-cyclisme.svg'"
      alt="Fédération française de cyclisme" />
    <h1 class="text-3xl sm:text-5xl/tight" i18n>Structurer, fédérer et animer une communauté de bénévoles à l'échelle d'une
      fédération sportive</h1>
  </div>

  <picture>
    <source [srcset]="assetsPath + 'federation-francaise-cyclisme-cyclisme-sur-piste.webp'" type="image/webp">
    <source [srcset]="assetsPath + 'federation-francaise-cyclisme-cyclisme-sur-piste.jpg'" type="image/jpg">
    <img class="mx-auto max-w-7xl w-full rounded-2xl sm:rounded-6xl mb-16" [src]="assetsPath + 'federation-francaise-cyclisme-cyclisme-sur-piste.jpg'"
      alt="Bénévoles championnats du monde de cyclisme sur piste" loading="lazy" />
  </picture>

  <div class="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto mb-16">
    <div class="flex-1 px-4 md:px-0 md:py-3 text-center">
      <p class="mb-4 text-6xl font-bold" i18n>300</p>
      <p i18n>bénévoles identifiés<br />pour Paris 2024</p>
    </div>
    <div class="h-2 w-4/5 self-center md:h-auto md:w-2 md:self-stretch bg-[#6DC94C] rounded"></div>
    <div class="flex-1 px-4 md:px-0 md:py-3 text-center">
      <p class="mb-4 text-6xl font-bold" i18n>7</p>
      <p i18n>événements<br /> affiliés à la FFC</p>
    </div>
    <div class="h-2 w-4/5 self-center md:h-auto md:w-2 md:self-stretch bg-[#6DC94C] rounded"></div>
    <div class="flex-1 px-4 md:px-0 md:py-3 text-center">
      <p class="mb-4 text-6xl font-bold" i18n>4</p>
      <p i18n>Championnats du monde</p>
    </div>
  </div>

  <p class="mx-auto max-w-3xl mb-8" i18n>
    La <a class="font-bold underline" href="https://www.ffc.fr/">Fédération Française de Cyclisme (FFC)</a> se trouve
    face à un défi majeur : la coordination efficace de milliers de
    bénévoles répartis sur ses multiples événements nationaux et internationaux dans diverses disciplines cyclistes.
    Pour répondre à ce besoin, la FFC a choisi d'adopter la solution logicielle Qoezion, développée par Quick-Off, qui a
    permis de relever ce défi sur deux axes stratégiques.
  </p>

  <h3 class="text-2xl mx-auto max-w-3xl mb-8" i18n>Identification et construction d'une communauté bénévole dynamique</h3>

  <p class="mx-auto max-w-3xl mb-8" i18n>
    La FFC cherchait à identifier, structurer et animer une communauté de bénévoles pour assurer la réussite de ses
    nombreux événements cyclistes. Avec une approche progressive, la FFC a utilisé <a class="font-bold underline"
      [routerLink]="NavigationService.Routes.Qoezion()">Qoezion</a> pour identifier les bénévoles
    qui se sont engagés sur différents événements, formant ainsi une communauté dynamique qui grandit au fur et à mesure
    que de nouveaux événements sont organisés par la fédération et ses membres.
  </p>

  <h3 class="text-2xl mx-auto max-w-3xl mb-8" i18n>Gestion optimale des bénévoles et duplication simplifiée</h3>

  <p class="mx-auto max-w-3xl mb-16" i18n>
    La diversité des disciplines et la portée des événements de la FFC exigent une gestion optimale des bénévoles.
    Qoezion a permis à la fédération de <strong>centraliser les informations de tous les bénévoles</strong>, de
    planifier les activités
    de chacun et de maintenir la cohérence de la coordination. De plus, la fonction de duplication d'événements offerte
    par Qoezion a grandement simplifié la gestion des bénévoles d'une édition à l'autre, en préservant les éléments clés
    tels que les formulaires de candidature, les activités, les créneaux horaires et les effectifs.
  </p>

  <qo-customer-story [story]="customerStory" theme="qomeet"></qo-customer-story>

  <p class="mx-auto max-w-3xl mb-8" i18n>
    Cette approche a abouti à plusieurs avantages stratégiques pour la FFC :
  </p>

  <p class="mx-auto max-w-3xl mb-8" i18n>- <strong>Vue holistique</strong> : La FFC a désormais une vue à 360 degrés sur sa communauté de bénévoles engagée dans tous les événements, ce qui facilite l'identification de bénévoles avec des compétences spécifiques pour chaque événement.</p>
  <p class="mx-auto max-w-3xl mb-8" i18n>- <strong>Visibilité pour les bénévoles</strong> : Les bénévoles ont une vue globale des événements pour lesquels la FFC recrute, ils peuvent ainsi rejoindre la communauté FFC et être notifiés des événements à venir.</p>
  <p class="mx-auto max-w-3xl mb-8" i18n>- <strong>Rationalisation et cohérence</strong> : La gestion centralisée des bénévoles permet à la FFC de planifier efficacement les activités de chaque bénévole, en évitant les chevauchements et en garantissant la cohérence du dispositif.</p>
  <p class="mx-auto max-w-3xl mb-8" i18n>- <strong>Fidélisation bénévole</strong> : L'engagement des bénévoles est également favorisé par la simplicité de leur participation grâce à leur compte Qoezion. Ils disposent ainsi d'un outil dédié pour communiquer avec les organisateurs et recevoir les informations essentielles de la fédération, contribuant ainsi à renforcer leur fidélité d'une édition à l'autre.</p>
  <p class="mx-auto max-w-3xl mb-16" i18n>La Fédération Française de Cyclisme a pu <strong>optimiser la gestion de sa communauté de bénévoles</strong> et la coordination de ses événements grâce à Qoezion, renforçant ainsi le succès et la qualité de ses compétitions cyclistes nationales et internationales.</p>

  <div class="flex mx-auto items-center justify-center max-w-7xl gap-4 sm:gap-12 md:gap-24 mb-24">
    <picture>
      <source [srcset]="assetsPath + 'cyclo-cross-federation-cyclisme.webp'" type="image/webp">
      <source [srcset]="assetsPath + 'cyclo-cross-federation-cyclisme.jpg'" type="image/jpg">
      <img class="rounded-2xl sm:rounded-6xl" [src]="assetsPath + 'cyclo-cross-federation-cyclisme.jpg'" alt="Cross mountain"
        loading="lazy" />
    </picture>
    <picture>
      <source [srcset]="assetsPath + 'competition-vtt-federation-cyclisme.webp'" type="image/webp">
      <source [srcset]="assetsPath + 'competition-vtt-federation-cyclisme.jpg'" type="image/jpg">
      <img class="rounded-2xl sm:rounded-6xl" [src]="assetsPath + 'competition-vtt-federation-cyclisme.jpg'" alt="Downhill" loading="lazy" />
    </picture>
  </div>

  <h2 class="mb-12 text-center text-3xl md:text-5xl/tight font-title  px-4 max-w-5xl mx-auto" i18n>Et maintenant, pourquoi pas
    vous ?</h2>

  <div class="mb-16">
    <qo-try-and-book></qo-try-and-book>
  </div>

  <div class="mb-8">
    <qo-poster-list [multiline]="true"></qo-poster-list>
  </div>


</main>
